import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ApiError, DrillResult, DrillSpec, EventAudit, ReportColumn, ReportResult } from '../api';
import { eventEndpoints, reportEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars, inZone } from './event/shared';
import { dateWords } from '../terms';

/**
 * Reports (docs/reporting.md, D-60): the catalog with bounded filters and
 * exports, drill-down into the rows beneath any cell, Event Audit with its
 * identities, background runs with their files, and schedules.
 */

type Tab = 'reports' | 'audit' | 'schedules' | 'runs';

export default function ReportsPage() {
  const { tab = 'reports' } = useParams<{ tab?: Tab }>();
  const can = usePermissions();
  if (!(can('reports.view_sales') || can('reports.view_financial') || can('reports.view_inventory') || can('reports.view_attendance'))) return <NotPermitted permission="reports.view_sales" />;
  return (
    <div className="space-y-4">
      <nav className="flex gap-1 border-b border-line text-sm">
        {(
          [
            ['reports', 'Reports'],
            ['audit', 'Event Audit'],
            ['schedules', 'Schedules'],
            ['runs', 'Runs'],
          ] as [Tab, string][]
        ).map(([id, label]) => (
          <Link key={id} to={id === 'reports' ? '/reports' : `/reports/${id}`} className={`-mb-px border-b-2 px-3 py-2 ${tab === id ? 'border-accent font-medium text-ink-900' : 'border-transparent text-ink-600 hover:text-ink-900'}`}>
            {label}
          </Link>
        ))}
      </nav>
      {tab === 'reports' ? <ReportRunner /> : null}
      {tab === 'audit' ? <AuditView /> : null}
      {tab === 'schedules' ? <Schedules /> : null}
      {tab === 'runs' ? <Runs /> : null}
    </div>
  );
}

// --- Shared: scope pickers -------------------------------------------------------------------------

/** Reports that take a `group_by`, and so offer the control (D-71). */
const GROUPED_REPORTS = new Set(['PAYMENT_METHOD_SUMMARY']);

/** How a payment summary's rows are cut. The default groups by tender alone. */
const GROUP_LABELS: [string, string][] = [
  ['', 'Tender'],
  ['channel', 'Channel and tender'],
  ['family', 'Tender family'],
  ['card_brand', 'Card brand'],
  ['event', 'Event'],
  ['performance', 'Start time'],
  ['date', 'Date'],
];

/** The moving windows the API resolves, in the order somebody looks for them. */
const PERIOD_LABELS: [string, string][] = [
  ['YESTERDAY', 'Yesterday'],
  ['TODAY', 'Today so far'],
  ['LAST_7_DAYS', 'Last 7 days'],
  ['LAST_30_DAYS', 'Last 30 days'],
  ['MONTH_TO_DATE', 'Month to date'],
  ['LAST_MONTH', 'Last month'],
  ['YEAR_TO_DATE', 'Year to date'],
];

/**
 * A report's scope. `dates: 'period'` swaps the two date boxes for a moving
 * window, which is the only thing that makes sense on a schedule: a repeating
 * report with fixed dates mails the same week out every morning (D-165).
 */
function useScope(options: { dates?: 'absolute' | 'period'; groupBy?: boolean } = {}) {
  const moving = options.dates === 'period';
  const [period, setPeriod] = useState('YESTERDAY');
  const [groupBy, setGroupBy] = useState('');
  const [eventId, setEventId] = useState('');
  const [performanceId, setPerformanceId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [channel, setChannel] = useState('');
  const events = useQuery({ queryKey: ['events-list'], queryFn: () => eventEndpoints.list() });
  const performances = useQuery({ queryKey: ['event', eventId, 'performances'], queryFn: () => eventEndpoints.performances(eventId), enabled: Boolean(eventId) });
  const filters = useMemo(
    () => ({ ...(eventId ? { event_id: eventId } : {}), ...(performanceId ? { performance_id: performanceId } : {}), ...(moving ? { period } : {}), ...(!moving && from ? { from } : {}), ...(!moving && to ? { to } : {}), ...(channel ? { sales_channel: channel } : {}), ...(options.groupBy && groupBy ? { group_by: groupBy } : {}) }),
    [eventId, performanceId, moving, period, from, to, channel, options.groupBy, groupBy],
  );
  const controls = (
    <div className="flex flex-wrap items-end gap-3">
      <label className="text-sm">
        <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Event</span>
        <select className={`mt-1 ${INPUT}`} value={eventId} onChange={(e) => { setEventId(e.target.value); setPerformanceId(''); }}>
          <option value="">Any event</option>
          {(events.data?.events ?? []).map((e) => (
            <option key={e.id} value={e.id}>
              {e.title}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{dateWords().One}</span>
        <select className={`mt-1 ${INPUT}`} value={performanceId} disabled={!eventId} onChange={(e) => setPerformanceId(e.target.value)}>
          <option value="">All {dateWords().many}</option>
          {(performances.data?.performances ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {inZone(p.starts_at, p.timezone)}
            </option>
          ))}
        </select>
      </label>
      {moving ? (
        <label className="text-sm">
          <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Period</span>
          <select className={`mt-1 ${INPUT}`} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIOD_LABELS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <>
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">From</span>
            <input type="date" className={`mt-1 ${INPUT}`} value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">To</span>
            <input type="date" className={`mt-1 ${INPUT}`} value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </>
      )}
      <label className="text-sm">
        <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Channel</span>
        <select className={`mt-1 ${INPUT}`} value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="">Any</option>
          {['WEB', 'BOX_OFFICE', 'PHONE', 'KIOSK', 'RENTER', 'ADMIN'].map((c) => (
            <option key={c} value={c}>
              {c.toLowerCase().replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </label>
      {options.groupBy ? (
        <label className="text-sm">
          <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Group by</span>
          <select className={`mt-1 ${INPUT}`} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            {GROUP_LABELS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
  return { filters, controls, eventId, performanceId, period, setPeriod, groupBy, setGroupBy, events: events.data?.events ?? [], performances: performances.data?.performances ?? [] };
}

function cell(column: ReportColumn, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (column.kind === 'cents') return dollars(Number(value));
  if (column.kind === 'pct') return `${Number(value).toFixed(1)}%`;
  if (column.kind === 'datetime') return formatWhen(String(value));
  return String(value);
}

function ResultTable({ result, onDrill }: { result: ReportResult; onDrill: (spec: DrillSpec) => void }) {
  return (
    <Table headers={result.columns.map((c) => c.label)}>
      {result.rows.length === 0 ? (
        <EmptyRow columns={result.columns.length}>No rows.</EmptyRow>
      ) : (
        result.rows.map((row, i) => (
          <tr key={i} className={`border-b border-line last:border-0 ${row.drill ? 'cursor-pointer hover:bg-page' : ''}`} onClick={() => row.drill && onDrill(row.drill)} title={row.drill ? 'Click to drill into the rows beneath this line' : undefined}>
            {result.columns.map((c) => (
              <td key={c.key} className={`px-5 py-2 text-sm ${c.kind === 'text' || c.kind === 'date' || c.kind === 'datetime' ? 'text-ink-900' : 'tabular text-right text-ink-600'}`}>
                {cell(c, row[c.key])}
              </td>
            ))}
          </tr>
        ))
      )}
      {result.totals ? (
        <tr className="bg-page font-semibold">
          {result.columns.map((c, i) => (
            <td key={c.key} className={`px-5 py-2 text-sm ${i === 0 ? '' : 'tabular text-right'}`}>
              {i === 0 ? 'Total' : c.key in result.totals! ? cell(c, result.totals![c.key]) : ''}
            </td>
          ))}
        </tr>
      ) : null}
    </Table>
  );
}

/** Exported so the dashboard drills through the same panel rather than a copy of it (D-304). */
export function DrillPanel({ spec, onClose }: { spec: DrillSpec; onClose: () => void }) {
  const [cursor, setCursor] = useState<string | null>(null);
  const [pages, setPages] = useState<DrillResult[]>([]);
  const page = useQuery<DrillResult, ApiError>({ queryKey: ['drill', spec, cursor], queryFn: () => reportEndpoints.drill({ ...spec, cursor, limit: 50 }) });
  useEffect(() => {
    if (page.data) setPages((p) => (p.some((x) => x === page.data) ? p : [...p, page.data!]));
  }, [page.data]);
  const rows = pages.flatMap((p) => p.rows);
  const columns = rows.length > 0 ? Object.keys(rows[0]!).filter((k) => !['id', 'order_id', 'order_item_id', 'journal_entry_id', 'financial_line_id', 'payment_id', 'refund_payment_id', 'original_line_id', 'ticket_id'].includes(k)) : [];
  const last = pages[pages.length - 1];
  return (
    <Panel title={`Drill-down · ${spec.kind}`} description="The rows beneath the cell you clicked, at their natural grain. Tickets link to their order." actions={<Button onClick={onClose}>Close</Button>}>
      {page.error ? <ErrorNote>{page.error.message}</ErrorNote> : null}
      <div className="overflow-x-auto">
        <Table headers={[...columns.map((c) => c.replaceAll('_', ' ')), '']}>
          {rows.length === 0 ? (
            <EmptyRow columns={columns.length + 1}>{page.isPending ? 'Loading…' : 'Nothing beneath this cell.'}</EmptyRow>
          ) : (
            rows.map((row, i) => (
              <tr key={String(row['id'] ?? i)} className="border-b border-line last:border-0">
                {columns.map((c) => (
                  <td key={c} className={`px-4 py-1.5 text-xs whitespace-nowrap ${typeof row[c] === 'number' ? 'tabular text-right' : ''}`}>
                    {typeof row[c] === 'number' && c.endsWith('_cents') ? dollars(row[c] as number) : row[c] === null || row[c] === undefined ? '' : String(row[c])}
                  </td>
                ))}
                <td className="px-4 py-1.5 text-xs">{row['order_id'] ? <Link to={`/orders/${String(row['order_id'])}`} className="text-accent hover:underline">order</Link> : null}</td>
              </tr>
            ))
          )}
        </Table>
      </div>
      {last?.next_cursor ? (
        <div className="border-t border-line px-5 py-3">
          <Button onClick={() => setCursor(last.next_cursor)}>More</Button>
        </div>
      ) : null}
    </Panel>
  );
}

// --- Reports -------------------------------------------------------------------------------------

function ReportRunner() {
  const can = usePermissions();
  const [code, setCode] = useState('SALES_BY_TICKET_TYPE');
  const scope = useScope({ groupBy: GROUPED_REPORTS.has(code) });
  const [drillSpec, setDrillSpec] = useState<DrillSpec | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const catalog = useQuery({ queryKey: ['reports', 'catalog'], queryFn: reportEndpoints.catalog });
  const run = useMutation({ mutationFn: () => reportEndpoints.run(code, scope.filters), onSuccess: () => setDrillSpec(null) });
  const background = useMutation({ mutationFn: (formats: ('CSV' | 'XLSX' | 'PDF')[]) => reportEndpoints.createRun({ report_type: code, filters: scope.filters, formats }), onSuccess: (r) => setNotice(`Run ${r.run_id.slice(-8)} queued; it appears under Runs with its files.`) });
  const definition = catalog.data?.reports.find((r) => r.code === code);
  const exportUrl = (format: string) => reportEndpoints.exportUrl(code, format, scope.filters);
  return (
    <div className="space-y-4">
      {notice ? <p className="rounded border border-ok/30 bg-ok/10 px-3 py-2 text-sm text-ok">{notice}</p> : null}
      <Panel title="Report" description={definition ? `Needs ${definition.requires.length ? definition.requires.join(' or ') : 'no scope'}; every number drills down to its rows.` : 'Choose a report.'}>
        <div className="flex flex-wrap items-end gap-3 px-5 py-4">
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Report</span>
            <select className={`mt-1 ${INPUT}`} value={code} onChange={(e) => setCode(e.target.value)}>
              {(catalog.data?.reports ?? []).filter((r) => r.code !== 'EVENT_AUDIT').map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          {scope.controls}
          <div className="ml-auto flex gap-2">
            <Button variant="primary" disabled={run.isPending} onClick={() => run.mutate()}>
              {run.isPending ? 'Running…' : 'Run'}
            </Button>
            {can('reports.export') ? (
              <>
                {['CSV', 'XLSX', 'PDF'].map((f) => (
                  <a key={f} href={exportUrl(f)} className="inline-flex min-h-9 items-center rounded border border-line px-3 text-sm font-medium hover:bg-page">
                    {f}
                  </a>
                ))}
                <Button onClick={() => background.mutate(['CSV', 'XLSX', 'PDF'])} disabled={background.isPending}>
                  Run in background
                </Button>
              </>
            ) : null}
          </div>
        </div>
        {run.error ? <ErrorNote>{(run.error as ApiError).message}</ErrorNote> : null}
        {run.data ? (
          <>
            <p className="px-5 pb-2 text-xs text-ink-400">
              {run.data.result.name} · as of {formatWhen(run.data.result.as_of)} · {run.data.result.rows.length} rows
            </p>
            <div className="overflow-x-auto">
              <ResultTable result={run.data.result} onDrill={setDrillSpec} />
            </div>
          </>
        ) : null}
      </Panel>
      {drillSpec ? <DrillPanel spec={drillSpec} onClose={() => setDrillSpec(null)} /> : null}
    </div>
  );
}

// --- Event Audit ---------------------------------------------------------------------------------

function AuditView() {
  const can = usePermissions();
  const scope = useScope();
  const [drillSpec, setDrillSpec] = useState<DrillSpec | null>(null);
  // The printed sheet's Sale Date Range (D-363); both blank means every sale.
  const [saleFrom, setSaleFrom] = useState('');
  const [saleTo, setSaleTo] = useState('');
  const audit = useQuery<{ audit: EventAudit }, ApiError>({ queryKey: ['audit', scope.eventId, scope.performanceId], queryFn: () => reportEndpoints.audit(scope.eventId, scope.performanceId || null), enabled: Boolean(scope.eventId) });
  const queryClient = useQueryClient();
  const recompute = useMutation({ mutationFn: () => reportEndpoints.recompute(scope.performanceId), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['audit'] }) });
  if (!can('reports.view_financial')) return <NotPermitted permission="reports.view_financial" />;
  const a = audit.data?.audit;
  const money = (n: number) => dollars(n);
  return (
    <div className="space-y-4">
      <Panel title="Event Audit" description="Computed from the raw facts as of now: inventory, sales, fees, tax, payments, accounting and attendance, then the identities that must hold exactly, in cents. Every ✗ is a bug or a data problem. Projections are drift-checked and repaired on every view." actions={a && scope.performanceId ? <Button onClick={() => recompute.mutate()} disabled={recompute.isPending}>Recompute projections</Button> : null}>
        <div className="px-5 py-4">{scope.controls}</div>
        {audit.error ? <ErrorNote>{audit.error.message}</ErrorNote> : null}
        {!scope.eventId ? <p className="px-5 pb-4 text-sm text-ink-600">Choose an event.</p> : null}
        {a ? (
          <div className="space-y-1 border-t border-line px-5 py-4 text-sm">
            <p className="text-xs text-ink-400">
              {a.event.title} · {a.event.venue_name} · {a.event.accounting_mode.toLowerCase().replaceAll('_', ' ')} · as of {formatWhen(a.as_of)}
              {can('reports.export') ? (
                <>
                  {' · '}
                  <a className="text-accent hover:underline" href={reportEndpoints.exportUrl('EVENT_AUDIT', 'PDF', { event_id: a.event.id, performance_id: a.performance?.id ?? null, sale_from: saleFrom || null, sale_to: saleTo || null })}>
                    PDF
                  </a>{' '}
                  <a className="text-accent hover:underline" href={reportEndpoints.exportUrl('EVENT_AUDIT', 'XLSX', { event_id: a.event.id, performance_id: a.performance?.id ?? null })}>
                    XLSX
                  </a>
                  <span className="ml-3 inline-flex items-center gap-1">
                    PDF sales from
                    <input type="date" aria-label="PDF sales from" value={saleFrom} onChange={(e) => setSaleFrom(e.target.value)} className="border border-line px-1 py-0.5 text-xs" />
                    to
                    <input type="date" aria-label="PDF sales to" value={saleTo} onChange={(e) => setSaleTo(e.target.value)} className="border border-line px-1 py-0.5 text-xs" />
                  </span>
                </>
              ) : null}
            </p>
            <ul className="mt-2 grid gap-1 md:grid-cols-2">
              {a.identities.map((i) => (
                <li key={i.id} className={`flex items-start gap-2 rounded border px-3 py-2 ${i.ok ? 'border-ok/30 bg-ok/10' : 'border-bad/30 bg-bad-soft'}`}>
                  <span className={`font-bold ${i.ok ? 'text-ok' : 'text-bad'}`}>{i.ok ? '✓' : '✗'}</span>
                  <span>
                    <button type="button" className="text-left font-medium text-ink-900 hover:underline" onClick={() => setDrillSpec(i.drill)}>
                      {i.id}. {i.name}
                    </button>
                    <span className="tabular block text-xs text-ink-600">
                      {i.left_label}: {i.id === 3 || i.id === 4 || i.id === 5 ? money(i.left) : i.left} · {i.right_label}: {i.id === 3 || i.id === 4 || i.id === 5 ? money(i.right) : i.right}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Panel>
      {a ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title={`A. Inventory · capacity ${a.inventory.capacity}`}>
            <Table headers={['Section', 'Price level', 'State', 'Hold', 'Units']}>
              {a.inventory.rows.map((r, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="px-5 py-1.5 text-sm">{r.section ?? '—'}</td>
                  <td className="px-5 py-1.5 text-sm">{r.price_zone ?? '—'}</td>
                  <td className="px-5 py-1.5 text-sm">{r.state}</td>
                  <td className="px-5 py-1.5 text-sm">{r.hold_class ?? ''}</td>
                  <td className="tabular px-5 py-1.5 text-right text-sm">{r.units}</td>
                </tr>
              ))}
            </Table>
          </Panel>
          <Panel title="B. Ticket sales">
            <Table headers={['Type', 'Level', 'Face', 'Channel', 'Sold', 'Comps', 'Refunded', 'Net revenue']}>
              {a.sales.rows.map((r, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="px-4 py-1.5 text-sm">{r.ticket_type ?? '—'}</td>
                  <td className="px-4 py-1.5 text-sm">{r.price_zone ?? '—'}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.face_value_cents)}</td>
                  <td className="px-4 py-1.5 text-sm">{r.sales_channel}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{r.sold}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{r.comps}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{r.refunded}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.net_cents)}</td>
                </tr>
              ))}
              <tr className="bg-page font-semibold">
                <td className="px-4 py-1.5 text-sm" colSpan={4}>
                  Total · discounts {money(a.sales.totals.discount_cents)} · comp value {money(a.sales.totals.comp_cents)}
                </td>
                <td className="tabular px-4 py-1.5 text-right text-sm">{a.sales.totals.sold}</td>
                <td className="tabular px-4 py-1.5 text-right text-sm">{a.sales.totals.comps}</td>
                <td className="tabular px-4 py-1.5 text-right text-sm">{a.sales.totals.refunded}</td>
                <td className="tabular px-4 py-1.5 text-right text-sm">{money(a.sales.totals.net_cents)}</td>
              </tr>
            </Table>
          </Panel>
          <Panel title="C. Fees and D. Tax">
            <Table headers={['Fee', 'Channel', 'Owner', 'Count', 'Charged', 'Reversed', 'Net']}>
              {[...a.fees.rows.map((r) => ({ ...r, kind: 'fee' })), ...a.tax.rows.map((r) => ({ name: r.name, sales_channel: '', owner: 'tax', count: null as number | null, charged_cents: r.charged_cents, reversed_cents: r.reversed_cents, net_cents: r.net_cents, kind: 'tax' }))].map((r, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="px-4 py-1.5 text-sm">{r.name}</td>
                  <td className="px-4 py-1.5 text-sm">{r.sales_channel}</td>
                  <td className="px-4 py-1.5 text-sm">{r.owner}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{r.count ?? ''}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.charged_cents)}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.reversed_cents)}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.net_cents)}</td>
                </tr>
              ))}
              {a.fees.rows.length + a.tax.rows.length === 0 ? <EmptyRow columns={7}>No fees or tax.</EmptyRow> : null}
            </Table>
          </Panel>
          <Panel title="E. Payments">
            <Table headers={['Tender', 'Brand', 'Channel', 'Collected', 'Refunded', 'Net']}>
              {a.payments.rows.map((r, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="px-4 py-1.5 text-sm">{r.tender_type}</td>
                  <td className="px-4 py-1.5 text-sm">{r.card_brand ?? ''}</td>
                  <td className="px-4 py-1.5 text-sm">{r.sales_channel}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.collected_cents)}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.refunded_cents)}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.net_cents)}</td>
                </tr>
              ))}
              {a.payments.rows.length === 0 ? <EmptyRow columns={6}>No payments.</EmptyRow> : null}
            </Table>
          </Panel>
          {a.payment_status ? (
            <Panel title="Payment status" description="Orders for this event by what has been paid (D-71). Order value is what customers owe in total; outstanding is still receivable.">
              <div className="flex flex-wrap gap-6 px-5 py-4 text-sm">
                <div><p className="text-xs text-ink-400 uppercase">Paid</p><p className="tabular text-lg font-semibold">{a.payment_status.paid_orders}</p></div>
                <div><p className="text-xs text-ink-400 uppercase">Partially paid</p><p className="tabular text-lg font-semibold">{a.payment_status.partially_paid_orders}</p></div>
                <div><p className="text-xs text-ink-400 uppercase">Unpaid</p><p className="tabular text-lg font-semibold">{a.payment_status.unpaid_orders}</p></div>
                <div><p className="text-xs text-ink-400 uppercase">Other</p><p className="tabular text-lg font-semibold">{a.payment_status.other_orders}</p></div>
                <div><p className="text-xs text-ink-400 uppercase">Order value</p><p className="tabular text-lg font-semibold">{money(a.payment_status.total_order_value_cents)}</p></div>
                <div><p className="text-xs text-ink-400 uppercase">Collected</p><p className="tabular text-lg font-semibold">{money(a.payment_status.collected_cents)}</p></div>
                <div><p className="text-xs text-ink-400 uppercase">Outstanding</p><p className={`tabular text-lg font-semibold ${a.payment_status.outstanding_cents ? 'text-warn' : ''}`}>{money(a.payment_status.outstanding_cents)}</p></div>
              </div>
            </Panel>
          ) : null}
          <Panel title={`F. Accounting summary · ${a.accounting.balanced ? 'balanced' : 'NOT BALANCED'}`}>
            <Table headers={['Code', 'Owner', 'Debits', 'Credits', 'Net']}>
              {a.accounting.rows.map((r, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="px-4 py-1.5 font-mono text-xs">{r.accounting_code}</td>
                  <td className="px-4 py-1.5 text-sm">{r.owner}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.debit_cents)}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.credit_cents)}</td>
                  <td className="tabular px-4 py-1.5 text-right text-sm">{money(r.net_cents)}</td>
                </tr>
              ))}
            </Table>
          </Panel>
          <Panel title="H. Attendance">
            <div className="grid grid-cols-3 gap-3 px-5 py-4 text-sm">
              {[
                ['Live tickets', a.attendance.live_tickets],
                ['Sold', a.attendance.sold],
                ['Comps', a.attendance.comps],
                ['Admitted', a.attendance.admitted],
                ['Comps scanned', a.attendance.comps_admitted],
                ['Re-scans', a.attendance.re_scans],
                ['No-shows', a.attendance.no_shows],
                ['Attendance', a.attendance.attendance_pct === null ? '—' : `${a.attendance.attendance_pct}%`],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
                  <p className="tabular text-xl font-bold text-ink-900">{value}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-line px-5 py-3 text-xs text-ink-600">
              Projections: {a.projections.filter((p) => !p.drift).length} of {a.projections.length} in agreement{a.projections.some((p) => p.drift) ? ` (${a.projections.filter((p) => p.drift).map((p) => p.projection).join(', ')} drifted and were repaired)` : ''}.
            </div>
          </Panel>
        </div>
      ) : null}
      {drillSpec ? <DrillPanel spec={drillSpec} onClose={() => setDrillSpec(null)} /> : null}
    </div>
  );
}

// --- Schedules -----------------------------------------------------------------------------------

function Schedules() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', report_type: 'DAILY_SALES', frequency: 'DAILY', local_time: '07:00', timezone: 'America/Chicago', weekdays: new Set<number>([0, 1, 2, 3, 4]), offset_minutes: '60', formats: new Set<string>(['PDF']), recipients: '' });
  const scope = useScope({ dates: 'period', groupBy: GROUPED_REPORTS.has(form.report_type) });
  const catalog = useQuery({ queryKey: ['reports', 'catalog'], queryFn: reportEndpoints.catalog });
  const schedules = useQuery({ queryKey: ['reports', 'schedules'], queryFn: reportEndpoints.schedules });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['reports', 'schedules'] });
  const create = useMutation({
    mutationFn: () => {
      const eventFrequency = ['EVENT_MORNING', 'AFTER_PERFORMANCE', 'POST_EVENT'].includes(form.frequency);
      if (eventFrequency && !scope.eventId) throw new Error('This frequency needs an event');
      const mask = form.frequency === 'WEEKLY' || form.frequency === 'SELECTED_WEEKDAYS' ? [...form.weekdays].reduce((m, d) => m | (1 << d), 0) : null;
      return reportEndpoints.createSchedule({
          name: form.name.trim(),
          report_type: form.report_type,
          filters: scope.filters,
          formats: [...form.formats],
          frequency: form.frequency,
          local_time: form.frequency === 'AFTER_PERFORMANCE' || form.frequency === 'POST_EVENT' ? null : form.local_time,
          timezone: form.timezone,
          weekdays_mask: mask,
          offset_minutes: form.frequency === 'AFTER_PERFORMANCE' || form.frequency === 'POST_EVENT' ? Number(form.offset_minutes) || 60 : null,
          event_id: scope.eventId || null,
          recipients: form.recipients.split(/[\s,;]+/).filter(Boolean).map((email) => ({ email })),
          enabled: true,
        });
    },
    onSuccess: () => {
      setError(null);
      setForm((f) => ({ ...f, name: '', recipients: '' }));
      refresh();
    },
    onError: (cause: Error) => setError(cause.message),
  });
  const toggle = useMutation({ mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => reportEndpoints.setScheduleEnabled(id, enabled), onSuccess: refresh, onError: (c: ApiError) => setError(c.message) });
  const runNow = useMutation({ mutationFn: (id: string) => reportEndpoints.runScheduleNow(id), onSuccess: refresh, onError: (c: ApiError) => setError(c.message) });
  if (!can('reports.schedule.manage')) return <NotPermitted permission="reports.schedule.manage" />;
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title="New schedule"
        description="A report, its scope, when it runs (in the venue's time zone) and who receives it. Files under 5 MB are attached; larger ones arrive as expiring links that need no sign-in."
        actions={
          <Button
            onClick={() => {
              // The daily cash-up, filled in (D-165). Payment Method Summary
              // grouped by channel is a row per channel and tender, which is
              // what finance reconciles the bank against; yesterday, because a
              // report sent at 7am is about the day that closed.
              setForm((f) => ({ ...f, name: 'Daily cash-up', report_type: 'PAYMENT_METHOD_SUMMARY', frequency: 'DAILY', local_time: '07:00', formats: new Set(['PDF', 'CSV']) }));
              scope.setPeriod('YESTERDAY');
              scope.setGroupBy('channel');
            }}
          >
            Daily cash-up
          </Button>
        }
      >
        <form
          className="space-y-4 px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Name</span>
              <input className={`mt-1 w-56 ${INPUT}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </label>
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Report</span>
              <select className={`mt-1 ${INPUT}`} value={form.report_type} onChange={(e) => setForm({ ...form, report_type: e.target.value })}>
                {(catalog.data?.reports ?? []).map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
            {scope.controls}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Frequency</span>
              <select className={`mt-1 ${INPUT}`} value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                {[['DAILY', 'Daily'], ['WEEKLY', 'Weekly'], ['SELECTED_WEEKDAYS', 'Selected weekdays'], ['EVENT_MORNING', `Morning of each ${dateWords().one}`], ['AFTER_PERFORMANCE', `After each ${dateWords().one}`], ['POST_EVENT', `Once after the final ${dateWords().one}`]].map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            {form.frequency === 'AFTER_PERFORMANCE' || form.frequency === 'POST_EVENT' ? (
              <label className="text-sm">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Minutes after the end</span>
                <input className={`tabular mt-1 w-24 ${INPUT}`} value={form.offset_minutes} onChange={(e) => setForm({ ...form, offset_minutes: e.target.value.replace(/\D/g, '') })} />
              </label>
            ) : (
              <label className="text-sm">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Local time</span>
                <input type="time" className={`mt-1 ${INPUT}`} value={form.local_time} onChange={(e) => setForm({ ...form, local_time: e.target.value })} />
              </label>
            )}
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Time zone</span>
              <input className={`mt-1 w-44 ${INPUT}`} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
            </label>
            {form.frequency === 'WEEKLY' || form.frequency === 'SELECTED_WEEKDAYS' ? (
              <div className="flex gap-2 pb-1 text-sm">
                {days.map((d, i) => (
                  <label key={d} className="flex items-center gap-1">
                    <input type="checkbox" checked={form.weekdays.has(i)} onChange={(e) => { const next = new Set(form.weekdays); if (e.target.checked) next.add(i); else next.delete(i); setForm({ ...form, weekdays: next }); }} /> {d}
                  </label>
                ))}
              </div>
            ) : null}
            <div className="flex gap-2 pb-1 text-sm">
              {['CSV', 'XLSX', 'PDF'].map((f) => (
                <label key={f} className="flex items-center gap-1">
                  <input type="checkbox" checked={form.formats.has(f)} onChange={(e) => { const next = new Set(form.formats); if (e.target.checked) next.add(f); else next.delete(f); setForm({ ...form, formats: next }); }} /> {f}
                </label>
              ))}
            </div>
          </div>
          <label className="block text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Recipients (emails, comma separated)</span>
            <input className={`mt-1 w-full max-w-2xl ${INPUT}`} value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} required />
          </label>
          <Button type="submit" variant="primary" disabled={create.isPending || form.formats.size === 0}>
            {create.isPending ? 'Saving…' : 'Create schedule'}
          </Button>
        </form>
      </Panel>
      <Panel title="Schedules">
        <Table headers={['Schedule', 'Report', 'When', 'Recipients', 'Next run', 'Runs', '']}>
          {(schedules.data?.schedules ?? []).length === 0 ? (
            <EmptyRow columns={7}>No schedules yet.</EmptyRow>
          ) : (
            schedules.data!.schedules.map((s) => (
              <tr key={s.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <div className="text-ink-900">{s.name}</div>
                  <Badge tone={s.enabled ? 'ok' : 'neutral'}>{s.enabled ? 'enabled' : 'disabled'}</Badge>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {s.report_type} {s.event_title ? `· ${s.event_title}` : ''} · {s.formats.join(', ')}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {s.frequency.toLowerCase().replaceAll('_', ' ')} {s.local_time ?? ''} {s.timezone}
                </td>
                <td className="px-5 py-3 text-xs text-ink-600">{s.recipients.join(', ')}</td>
                <td className="px-5 py-3 text-xs whitespace-nowrap text-ink-600">{s.next_run_at ? formatWhen(s.next_run_at) : '—'}</td>
                <td className="tabular px-5 py-3 text-sm">{s.runs}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <Button className="mr-1" onClick={() => runNow.mutate(s.id)} disabled={runNow.isPending}>
                    Run now
                  </Button>
                  <Button onClick={() => toggle.mutate({ id: s.id, enabled: !s.enabled })}>{s.enabled ? 'Disable' : 'Enable'}</Button>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

// --- Runs ----------------------------------------------------------------------------------------

function Runs() {
  const can = usePermissions();
  const runs = useQuery({ queryKey: ['reports', 'runs'], queryFn: reportEndpoints.runs, refetchInterval: 5000 });
  if (!can('reports.export')) return <NotPermitted permission="reports.export" />;
  return (
    <Panel title="Runs" description="Background exports and scheduled deliveries, newest first. Files are kept for 90 days.">
      <Table headers={['Requested', 'Report', 'Scope', 'Status', 'Rows', 'Files']}>
        {(runs.data?.runs ?? []).length === 0 ? (
          <EmptyRow columns={6}>No runs yet.</EmptyRow>
        ) : (
          runs.data!.runs.map((r) => (
            <tr key={r.id} className="border-b border-line last:border-0">
              <td className="px-5 py-3 text-xs whitespace-nowrap text-ink-600">
                {formatWhen(r.created_at)}
                <div className="text-ink-400">{r.schedule_id ? 'schedule' : (r.requested_by ?? 'staff')}</div>
              </td>
              <td className="px-5 py-3 text-sm text-ink-900">{r.report_type}</td>
              <td className="px-5 py-3 text-xs text-ink-600">{Object.entries(r.params.filters).filter(([, v]) => v).map(([k, v]) => `${k}=${String(v).slice(0, 12)}`).join(' ')}</td>
              <td className="px-5 py-3">
                <Badge tone={r.status === 'SUCCEEDED' ? 'ok' : r.status === 'FAILED' ? 'bad' : 'neutral'}>{r.status}</Badge>
                {r.error ? <div className="text-xs text-bad">{r.error}</div> : null}
              </td>
              <td className="tabular px-5 py-3 text-sm">{r.row_count ?? ''}</td>
              <td className="px-5 py-3 text-sm">
                {r.formats.map((f) => (
                  <a key={f} href={reportEndpoints.runFileUrl(r.id, f)} className="mr-2 text-accent hover:underline">
                    {f}
                  </a>
                ))}
              </td>
            </tr>
          ))
        )}
      </Table>
    </Panel>
  );
}
